// @flow

import React, {Component} from 'react';
import _ from 'lodash';
import mapSeries from 'async/mapSeries';

import Search from './Search.jsx';
import TreeItemDir from './TreeItemDir.jsx';
import Wait from './Wait.jsx';
import App from './App.jsx';

import {ROOT_DIR} from './consts.js';
import {sub_paths, path_is_inside_bi, takeDir, takeExtension, no_rclick, noop} from './utils.js';
import {T_DIR, T_CULTI} from './codtype.js';

import type {T_OpenNextDir} from './TreeItemDir.jsx';
import type {T_Next, T_Item} from './types.js';

import './css/Tree.css';


type T_TreeProps = {
  filelist  : Array<T_Item>,
  search_ui : boolean,
  height    : number,
  pop_height: number,
  show_ms   : boolean,
  disabled  : boolean,
  app       : App
};


type T_TreeState = {
  filelist   : Array<T_Item>,
  restrict_to: Array<string>,
  searched   : boolean,
  waiting    : boolean
};



export default class Tree extends Component<T_TreeProps, T_TreeState> {
  item_dirs: Array<TreeItemDir>;
  search: ?Search;
  
  
  
  constructor(props: T_TreeProps) {
    super(props);
    
    this.state = {
      filelist   : this.props.filelist,
      restrict_to: [],
      searched   : false,
      waiting    : false
    };
    
    this.item_dirs = [];
    
    props.app.tree = this;
  }
  
  
  
  componentWillUnmount() {
    this.props.app.tree = null;
  }
  
  
  
  componentDidMount() {
    const ready = () => this.props.app.ready('Tree');
    
    if (this.props.app.on_root() && this.item_dirs.length > 0) {
      this.item_dirs[0].toggle(ready);
    }
    else {
      ready();
    }
  }
  
  
  
  open_chain(ps: Array<string>, mb_item_dir: ?TreeItemDir, next: T_OpenNextDir): void {
    if (ps.length === 0) {
      return next(mb_item_dir);
    }
    
    if (mb_item_dir != null) {
      const item_dir = mb_item_dir;
      
      item_dir.open((res) => {
        const new_ps = _.tail(ps);
        
        if (new_ps.length > 0 && takeExtension(new_ps[0]) === 'shp') {
          const item_shp = item_dir.shps.find(shp => shp.props.node.Path === new_ps[0]);
          
          if (item_shp != null) {
            item_shp.open(() => next(item_dir));
          }
        }
        else {
          const new_item_dir = item_dir.dirs.find(item => item.props.node.Path === new_ps[0]);
          
          if (new_item_dir != null) {
            this.open_chain(new_ps, new_item_dir, next);
          }
          else {
            next(item_dir);
          }
        }
      });
    }
    else {
      next(mb_item_dir);
    }
  }
  
  
  
  open_path(path: string, next: T_OpenNextDir): void {
    if (! path) {
      return next(null);
    }
    
    let ps = sub_paths(path);
    
    const on_root = this.props.app.on_root();
    
    if (ps.length > 0 && takeExtension(ps[0]) === 'dir') {
      let item_dir = this.item_dirs.find(item => item.props.node.Path === ps[0]);
      
      if (on_root && ! item_dir) {
        item_dir = this.item_dirs[0];
        ps.unshift(ROOT_DIR);
      }
      
      if (! item_dir) {
        ps = _.dropWhile(ps, (pp) => {
          item_dir = this.item_dirs.find(idir => idir.props.node.Path === pp);
          
          return item_dir == null;
        });
      }
      
      this.open_chain(ps, item_dir, next);
    }
    else {
      next(null);
    }
  }
  
  
  
  get_sub_dir(dir: string): ?TreeItemDir {
    for (const item_dir of this.item_dirs) {
      const res = item_dir.get_sub_dir(dir);
      
      if (res != null) {
        return res;
      }
    }
    
    return null;
  }
  
  
  
  hilight_shp(path: string, hilight: boolean, scroll: boolean, next: T_Next = noop) {
    const item_dir = this.get_sub_dir(takeDir(path));
    
    if (item_dir != null) {
      const item_shp = item_dir.shps.find(shp => shp.props.node.Path === path);
      
      if (item_shp != null) {
        const item = item_shp;
        
        item.setState({hilight: hilight && item.props.node.Cod === T_CULTI}, () => {
          if (hilight && scroll) {
            item.scroll();
          }
          
          next(true);
        });
      }
      else {
        next(false);
      }
    }
    else {
      next(false);
    }
  }
  
  
  
  open_these(dirs: Array<string>, next: T_Next = noop): void {
    mapSeries(dirs, (dir: string, callback) => {
      this.open_path(dir, () => callback(null, true));
    }, (err, rs) => next(true));
  }
  
  
  
  found(path: string): boolean {
    return (
      this.state.restrict_to.length === 0 ?
        true
      :
        _.some(this.state.restrict_to, re => path_is_inside_bi(re, path))
    );
  }
  
  
  
  open_root(next: T_Next = noop): void {
    next(false);
  }
  
  
  
  unclick_all(): void {      // NOTE: superficialmente desclickea todo, sin llamar a close()
    this.item_dirs.forEach(item_dir => item_dir.unclick_all());
  }
  
  
  
  render() {
    const tree_sty = {
      position : 'absolute',
      top      : 40,
      overflow : 'auto',
      maxHeight: this.props.pop_height - 48,
      width    : 275
    };
    
    const display_tree = {
      display: (this.state.waiting || (this.state.restrict_to.length === 0 && this.state.searched) ? 'none' : 'block')
    };
    
    this.item_dirs = [];
    
    return (
      <div onContextMenu={no_rclick}>
        <div className={this.props.search_ui ? 'search_panel' : 'hide'}>
          <Search
            waiting={this.state.waiting}
            app={this.props.app}
            tree={this}
            disabled={this.props.disabled}
            ref={search => (this.search = search)}
            cls='integrated_search'
          />
        </div>
        
        {this.state.waiting &&
          <div>
            <Wait />
          </div>
        }
        
        {(this.state.restrict_to.length === 0 && this.state.searched) &&
          <p className='wait'>Sin resultados.</p>
        }
        
        <div style={_.extend(this.props.search_ui ? tree_sty : {}, display_tree)}>
          {this.state.filelist.filter(item => this.found(item.Path)).map((item) => {
            return (
              <TreeItemDir
                node={item}
                indent={0}
                restrict_to={item.Cod === T_DIR ? this.state.restrict_to : []}
                show_me={true}
                parent_item={null}
                app={this.props.app}
                key={item.Path.slice(6)}
                show_ms={this.props.show_ms}
                disabled={this.props.disabled}
                ref={(comp) => {
                  if (comp != null) {
                    this.item_dirs.push(comp);
                  }
                }}
              />
            );
          })}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/Tree.jsx